import { useState } from 'react';
import { Button, type ButtonProps } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Heart, Loader2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRegisterRouteInterest } from '@/hooks/useSelfPacedRoutes';
import { useToast } from '@/hooks/use-toast';
import { PhoneCountryInput, buildE164 } from '@/components/PhoneCountryInput';

interface Props {
  programId: string;
  programName: string;
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
  className?: string;
}

const E164_RE = /^\+[1-9]\d{7,14}$/;

/**
 * CTA único "Estoy Interesado" para rutas con acompañamiento.
 * El celular / WhatsApp es obligatorio para que ventas contacte al prospecto.
 * - Sin sesión: modal con nombre, correo y celular.
 * - Con sesión y celular válido en el perfil: 1 clic.
 * - Con sesión sin celular: modal que pide solo el celular.
 */
export function RouteInterestButton({ programId, programName, size = 'sm', variant = 'default', className }: Props) {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const register = useRegisterRouteInterest();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('57');
  const [phoneLocal, setPhoneLocal] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const profilePhone = profile?.phone && E164_RE.test(profile.phone) ? profile.phone : null;

  const success = () => {
    setDone(true);
    setOpen(false);
    toast({
      title: '¡Gracias por tu interés!',
      description: `Te contactaremos con la información de ${programName}.`,
    });
  };

  const onError = (err: any) =>
    toast({ title: 'No pudimos registrar tu interés', description: err.message, variant: 'destructive' });

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (done) return;
    if (!user || !profilePhone) {
      setOpen(true);
      return;
    }
    register.mutate({ programId, phone: profilePhone }, { onSuccess: success, onError });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneLocal.trim()) {
      setPhoneError('El número de celular es obligatorio para poder contactarte.');
      return;
    }
    const phone = buildE164(countryCode, phoneLocal);
    if (!phone) {
      setPhoneError('Ingresa un número de celular válido (solo dígitos, sin el indicativo).');
      return;
    }
    setPhoneError(null);
    register.mutate(
      user ? { programId, phone } : { programId, fullName, email, phone },
      { onSuccess: success, onError },
    );
  };

  return (
    <>
      <Button
        size={size}
        variant={done ? 'secondary' : variant}
        className={className}
        onClick={handleClick}
        disabled={register.isPending || done}
      >
        {register.isPending ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : done ? (
          <CheckCircle2 className="w-4 h-4 mr-2" />
        ) : (
          <Heart className="w-4 h-4 mr-2" />
        )}
        {done ? 'Interés registrado' : 'Estoy Interesado'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" onClick={e => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Estoy interesado en {programName}</DialogTitle>
            <DialogDescription>
              {user
                ? 'Déjanos tu celular y te contactamos por WhatsApp con fechas, precios y detalles de la próxima cohorte.'
                : 'Déjanos tus datos y te contactamos con fechas, precios y detalles de la próxima cohorte.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!user && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="ri-name">Nombre completo</Label>
                  <Input id="ri-name" value={fullName} onChange={e => setFullName(e.target.value)} required maxLength={200} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ri-email">Correo electrónico</Label>
                  <Input id="ri-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="ri-phone">Número de celular / WhatsApp</Label>
              <PhoneCountryInput
                id="ri-phone"
                countryCode={countryCode}
                onCountryCodeChange={setCountryCode}
                value={phoneLocal}
                onChange={v => { setPhoneLocal(v); if (phoneError) setPhoneError(null); }}
                invalid={!!phoneError}
              />
              {phoneError
                ? <p className="text-xs text-destructive">{phoneError}</p>
                : <p className="text-xs text-muted-foreground">Te escribiremos por WhatsApp a este número.</p>}
            </div>
            <DialogFooter>
              <Button type="submit" disabled={register.isPending} className="w-full">
                {register.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Enviar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default RouteInterestButton;
